// /finance?tab=jobs (finance-real, the user 2026-10-22: "a new section for all the jobs for all the clients"): every
// client's jobs in one table, the client page's Jobs table with a Client column. The header's right holds the same status
// tabs, Draft | Quoting | Agreed | Active | Paused | Done | Invoiced | Dropped with their counts (`show`, Active by default while it has
// jobs, else the first status with any, showTab; DD-062), and
// the footer the tab's totals, the server's (TD-102). A row opens the client page's job drawer on this page
// (`modal=line-job&lineJob=`, the page's AgencyLineJobDrawer, mounted on every tab so To do's rows open it too), its status the shared status dropdown on the row and in the drawer. The agency's own client's
// jobs are listed under Agency (the user 2026-10-24): their figure is what their claims cost, the client page's Costs, with no margin
// (TD-105), and the footer adds those costs apart. A job's quote opens on its client's page, where quotes live; its invoice opens on Invoices.
// The header's New job (`modal=new-job`, AgencyNewJobButton) opens the job form with the client picked first, Agency (no
// client) listed first and nothing picked to begin with: "an agency job I add that has no client but shows in the global jobs list". Made, the job's
// drawer opens in the form's place, as the client page's New job hands over.
import { useNavigate } from "react-router-dom";
import { TablePageBody } from "@/components/PageLayout/TablePageBody";
import { BadgeDot } from "@/components/ui/Badge";
import { DataTable, type DataColumn } from "@/components/ui/DataTable";
import type { DropdownMenuEntry } from "@/components/ui/DropdownMenu";
import { MoneyText } from "@/components/ui/MoneyText";
import { TabGroup, type TabGroupConfig } from "@/components/ui/TabGroup";
import { ENTITY_CONFIG, EntityCreateButton } from "@/core/entityConfig";
import { useTrailNavOptions } from "@/core/navigation/trail";
import { TabPending } from "../components/TabPending";
import { TotalsFooter, type TotalItem } from "../components/TotalsFooter";
import { jobTotalsOf } from "../clientPage/ClientFinancePage";
import { JobStatusBadge } from "../clientPage/JobStatusBadge";
import { needsStart } from "../clientPage/LineJobDrawer";
import { LineJobFormDrawer } from "../clientPage/LineJobForms";
import { JOB_STATUS, JOB_STATUSES } from "../clientPage/lineJobText";
import { ReviewMark } from "../clientPage/ReviewMark";
import { useFinanceParams, type ParamPatch } from "../hooks/useFinanceParams";
import type { AgencyJobsResponse, AgencyJobTotals, AgencyLineJob, LineJobPickedStatus, LineJobStatus } from "../types";
import { clientName, jobSubline } from "../utils/names";
import { showTab } from "../utils/showTabs";
import { useAgencyJobsShowTabs } from "./hooks/useAgencyJobsShowTabs";
import { useAgencyJobsTab } from "./hooks/useAgencyJobsTab";

// The user's job, a line item, rides main's `contract` key (the entityConfig shadow).
const JOB = ENTITY_CONFIG.contract;
const jobWord = JOB.label.toLowerCase();
const CLIENT = ENTITY_CONFIG.client;
const quoteWord = ENTITY_CONFIG.quote.label.toLowerCase();
const invoiceWord = ENTITY_CONFIG.invoice.label.toLowerCase();

/** This tab's drawer params, cleared whenever it opens another. */
const NO_DRAWER: ParamPatch = { modal: null, lineJob: null, invoice: null, confirm: null };

/** The tab shown: the URL's, else Active while it has jobs, else the first status with any (showTab). */
export const toJobShow = (value: string | null, totals?: AgencyJobsResponse["jobTotals"]): LineJobStatus =>
  showTab(value, JOB_STATUSES, "active", totals ? (s) => totals[s].count : null);

const EMPTY: Record<LineJobStatus, string> = {
  draft: `No draft ${jobWord} on any ${CLIENT.label.toLowerCase()}.`,
  quoting: `No ${jobWord} on a proposed ${quoteWord}.`,
  agreed: `No ${jobWord} agreed and waiting to start.`,
  active: `No active ${jobWord}.`,
  paused: `No ${jobWord} paused.`,
  done: `No ${jobWord} done and waiting for an ${invoiceWord}.`,
  invoiced: `No ${jobWord} on an ${invoiceWord} yet.`,
  dropped: `No ${jobWord} dropped.`,
};

/** The client page's footer for the tab, over the clients that bill, and the agency's own client's costs apart, but on
 * Dropped, which only names what was dropped. */
function totalsOf(t: AgencyJobTotals, status: LineJobStatus): TotalItem[] {
  return [...jobTotalsOf(t, status, false), ...(status !== "dropped" && t.ownCostsMinor !== 0 ? [{ label: `Own ${CLIENT.label.toLowerCase()}'s costs`, amountMinor: t.ownCostsMinor }] : [])];
}

/** It is on a draft or a proposed quote, whose status sets its own (the drawer's `onOpenQuote`). */
const onOpenQuote = (j: AgencyLineJob): boolean => j.quoteStatus === "draft" || j.quoteStatus === "proposed";

/** A status track on a phone: eight tabs with their counts pass 390px, so each keeps its whole label (shrink-0) and the
 * track scrolls sideways with no scrollbar, as main's page tabs do (AgencyFinancePage, DD-058), never truncating. */
export const STATUS_TRACK = "min-w-0 max-w-full overflow-x-auto [scrollbar-width:none]";

/** The header's status tabs, the client page's Jobs tabs: each status with its count. */
export function AgencyJobsShowTabs({ config }: { config: TabGroupConfig }) {
  const { params, set } = useFinanceParams();
  const data = useAgencyJobsShowTabs().data;
  return (
    <TabGroup
      items={JOB_STATUSES.map((s) => ({ id: s, label: JOB_STATUS[s].label, badge: data ? data.jobTotals[s].count : undefined, className: "shrink-0" }))}
      activeId={toJobShow(params.get("show"), data?.jobTotals)}
      onTabChange={(id) => set({ show: id }, true)}
      config={config}
      className={STATUS_TRACK}
    />
  );
}

/** The header's New job: the job form, its client picked first. */
export function AgencyNewJobButton({ onOpen }: { onOpen?: () => void }) {
  const { set } = useFinanceParams();
  return (
    <EntityCreateButton
      entity="contract"
      variant="primary"
      size="sm"
      onClick={() => {
        onOpen?.();
        set({ ...NO_DRAWER, modal: "new-job" });
      }}
    />
  );
}

interface Props {
  /** Called as a row opens a drawer, so the page gives focus back to it once the drawers close (useOpenerFocus). */
  onOpen?: () => void;
}

export function AgencyJobsTab({ onOpen }: Props) {
  const { params, set, closeModals } = useFinanceParams();
  const navigate = useNavigate();
  const trail = useTrailNavOptions(ENTITY_CONFIG.finance.label);
  const creating = params.get("modal") === "new-job";
  const { jobsQuery, setStatus, newJobClients } = useAgencyJobsTab(creating);
  const data = jobsQuery.data;
  const status = toJobShow(params.get("show"), data?.jobTotals);
  const jobs = data?.jobs ?? [];
  const go = (patch: ParamPatch, replace?: boolean) => {
    onOpen?.();
    set({ ...NO_DRAWER, ...patch }, replace);
  };
  /** Its client's page, where its quote lives, with that drawer open. */
  const onClientPage = (j: AgencyLineJob, query: Record<string, string>) => navigate(`/app/${j.client.id}/finance?${new URLSearchParams(query).toString()}`, trail);

  // A job's steps: its invoice opens on Invoices, its quote and the job itself on its client's page.
  const actionsOf = (j: AgencyLineJob): DropdownMenuEntry[] => {
    const items: DropdownMenuEntry[] = [];
    if (j.quoteId) {
      const quoteId = j.quoteId;
      items.push({ id: "quote", label: `Open its ${quoteWord}`, onClick: () => onClientPage(j, { view: "quotes", modal: "quote", quote: quoteId }) });
    }
    if (j.invoice) {
      const ref = j.invoice;
      items.push({ id: "invoice", label: `Open its ${invoiceWord}`, onClick: () => go({ tab: "invoices", show: ref.status, modal: "job-invoice", invoice: ref.id }) });
    }
    items.push({ id: "page", label: `Open ${j.client.name}'s page`, onClick: () => onClientPage(j, { modal: "line-job", lineJob: j.id }) });
    return items;
  };

  // A row's status pick: Active for a job not started asks its day in the drawer, as on the client page.
  const pickStatus = (j: AgencyLineJob, picked: LineJobPickedStatus) => {
    if (picked === "active" && needsStart(j)) return go({ modal: "line-job", lineJob: j.id, confirm: "start" });
    setStatus.mutate({ jobId: j.id, input: { status: picked, startsOn: null } });
  };

  const columns: DataColumn<AgencyLineJob>[] = [
    {
      key: "job", label: JOB.label, truncates: true,
      render: (j) => (
        <span className="flex items-center gap-2 min-w-0">
          {/* DD-055: below sm the Status column hides, and the status rides the name as a dot. */}
          <BadgeDot tone={JOB_STATUS[j.status].tone} label={JOB_STATUS[j.status].label} className="sm:hidden" />
          <span className="flex flex-col min-w-0">
            <span className="font-medium text-gray-900 truncate">{j.name}</span>
            {/* jobSubline's order, client first. DD-055: the client segment shows below md only, where the Client column
                hides; from md the column names it, so the subline never says it twice. */}
            <span className="text-xs text-gray-500 truncate">
              <span className="md:hidden">{`${clientName(j.client.name, j.ownClient)} · `}</span>
              {jobSubline(j)}
            </span>
          </span>
        </span>
      ),
    },
    {
      key: "client", label: CLIENT.label, width: "hidden md:table-cell w-48",
      render: (j) => <span className="block truncate text-gray-700">{clientName(j.client.name, j.ownClient)}</span>,
    },
    {
      key: "state", label: "Status", width: "hidden sm:table-cell w-32",
      render: (j) => (
        // The row opens the drawer; the dropdown is the cell's own, as on an idea row.
        <span onClick={(e) => e.stopPropagation()}>
          <JobStatusBadge job={j} onOpenQuote={onOpenQuote(j)} onPick={(s) => pickStatus(j, s)} loading={setStatus.isPending && setStatus.variables?.jobId === j.id} />
        </span>
      ),
    },
    {
      key: "who", label: "Who", width: "hidden lg:table-cell w-44",
      render: (j) => (
        <span className="block truncate text-gray-600">
          {j.who.join(", ") || "·"}
          {j.overCap && <span className="text-xs text-amber-700">{" · over the cap"}</span>}
        </span>
      ),
    },
    {
      // The agency's own client bills nothing: its figure is what its claims cost (TD-105), the client page's Costs column,
      // and says so.
      key: "price", label: "Price", align: "right", width: "w-32",
      render: (j) => (
        <ReviewMark review={j.review}>
          {j.ownClient ? (
            <span className="flex flex-col items-end">
              <MoneyText amountMinor={j.claimedMinor} />
              <span className="text-xs text-gray-500">costs</span>
            </span>
          ) : (
            <MoneyText amountMinor={j.priceMinor} />
          )}
        </ReviewMark>
      ),
    },
    {
      key: "margin", label: "Margin", align: "right", width: "hidden sm:table-cell w-32",
      render: (j) =>
        j.ownClient ? (
          <span className="text-gray-400">·</span>
        ) : (
          <ReviewMark review={j.review}>
            <MoneyText amountMinor={j.marginMinor} balance />
          </ReviewMark>
        ),
    },
  ];

  const footer = data ? totalsOf(data.jobTotals[status], status) : [];
  return (
    <>
      <TablePageBody>
        {!data ? (
          <TabPending failed={jobsQuery.isError} message={`Could not load the ${JOB.plural.toLowerCase()}.`} retrying={jobsQuery.isFetching} onRetry={() => void jobsQuery.refetch()} />
        ) : (
          <DataTable
            columns={columns}
            rows={jobs.filter((j) => j.status === status)}
            rowKey={(j) => j.id}
            onRowClick={(j) => go({ modal: "line-job", lineJob: j.id })}
            rowMenu={(j) => actionsOf(j)}
            empty={EMPTY[status]}
            footer={footer.length > 0 ? <TotalsFooter items={footer} /> : undefined}
          />
        )}
      </TablePageBody>
      {data && creating && newJobClients && (
        <LineJobFormDrawer
          clientId=""
          own={false}
          today={data.today}
          forAgency={{ clients: newJobClients }}
          // The job's drawer in the form's place (DD-045), on the tab the job lists under.
          onSaved={(job) => set({ ...NO_DRAWER, show: job.status, modal: "line-job", lineJob: job.id }, true)}
          onClose={() => closeModals(NO_DRAWER)}
        />
      )}
    </>
  );
}
